import {
  NoAccountsOutlined,
  ProductionQuantityLimitsOutlined,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  Pagination,
  Paper,
  Typography,
  useTheme,
  Fade,
  Grow,
  Zoom,
  styled,
} from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import ExhibitorCard from "./ExhibitorCard";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[4],
  transition: "all 0.3s ease",
}));

const AnimatedGrid = styled(Grid)(({ theme }) => ({
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
}));

export default function FilteredExhibitors({
  refresh,
  filteredExhibitors,
  paginatedExhibitors,
  itemsPerPage,
  currentPage,
  handlePageChange,
  setFilteredExhibitors,
  role,
  onEdit,
  onDelete,
  user,
}) {
  const theme = useTheme();
  const { t } = useTranslation();

  const handleExhibitorChange = (updatedExhibitor) => {
    setFilteredExhibitors((prevExhibitors) =>
      prevExhibitors.map((exhibitor) =>
        exhibitor.id === updatedExhibitor.id ? updatedExhibitor : exhibitor
      )
    );
  };

  useEffect(() => {}, [filteredExhibitors]);

  return (
    <Box sx={{ position: "relative" }}>
      <StyledPaper
        style={{ width: "100%", padding: "10px 10px" }}
        elevation={3}
      >
        {filteredExhibitors.length === 0 ? (
          <Fade in timeout={500}>
            <Box
              minHeight="50vh"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
            >
              <Zoom in timeout={800}>
                {role === "products" ? (
                  <ProductionQuantityLimitsOutlined
                    sx={{
                      fontSize: 120,
                      color: theme.palette.text.disabled,
                      mb: 2,
                      opacity: 0.8,
                    }}
                  />
                ) : (
                  <NoAccountsOutlined
                    sx={{
                      fontSize: 120,
                      color: theme.palette.text.disabled,
                      mb: 2,
                      opacity: 0.8,
                    }}
                  />
                )}
              </Zoom>
              <Typography
                variant="h5"
                component="div"
                color="textSecondary"
                sx={{
                  fontWeight: 500,
                  maxWidth: 400,
                  lineHeight: 1.4,
                  mb: 2,
                }}
              >
                {t(`no${role}Found`)}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ opacity: 0.8 }}
              >
                {t("tryAdjustingFilters")}
              </Typography>
            </Box>
          </Fade>
        ) : (
          <Grid container spacing={3}>
            {paginatedExhibitors?.map((exhibitor, index) => (
              <AnimatedGrid
                item
                xs={12}
                sm={6}
                md={4}
                lg={4}
                key={exhibitor.id || index}
                sx={{ width: "100%", padding: "0 0px" }}
              >
                <Grow in timeout={index * 100 + 500}>
                  <div>
                    <ExhibitorCard
                      exhibitor={exhibitor}
                      Exhibitors={paginatedExhibitors}
                      onExhibitorChange={handleExhibitorChange}
                      refresh={refresh}
                      role={role}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      user={user}
                    />
                  </div>
                </Grow>
              </AnimatedGrid>
            ))}
          </Grid>
        )}
      </StyledPaper>

      {filteredExhibitors.length > 0 && (
        <Fade in timeout={800}>
          <Box
            sx={{
              mt: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Pagination
              count={Math.ceil(filteredExhibitors.length / itemsPerPage)}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                },
                "& .Mui-selected": {
                  boxShadow: theme.shadows[2],
                },
              }}
            />
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ opacity: 0.8 }}
            >
              {t("showingResults", {
                from: (currentPage - 1) * itemsPerPage + 1,
                to: Math.min(
                  currentPage * itemsPerPage,
                  filteredExhibitors.length
                ),
                total: filteredExhibitors.length,
              })}
            </Typography>
          </Box>
        </Fade>
      )}
    </Box>
  );
}

FilteredExhibitors.propTypes = {
  filteredExhibitors: PropTypes.array.isRequired,
  paginatedExhibitors: PropTypes.array.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  setFilteredExhibitors: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
